import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { navigate } from 'gatsby';
import classnames from 'classnames';
import useIsMobile from 'hooks/useIsMobile';
import { routes, faqRoutes, locales as unfilteredLocales, languages as languageMetadata } from 'languages';
import { StorageCookie, Language, PageType } from 'enums/types';
import useOnClickOutside from 'hooks/useOnClickOutside';
import accessibleDropdownKeyboardHandler from 'helpers/accessibleDropdownKeyboardHandler';
import * as style from './languageSwitcher.module.scss';

interface Props {
	popover: boolean;
	originalPath: string;
	pageType: PageType;
}

const locales = process.env.GATSBY_BUILD_ALL_LANGUAGES === 'true' ?
	unfilteredLocales : unfilteredLocales.filter((locale) => locale === Language.Default);

const LanguageSwitcher = ({ originalPath, pageType, popover }: Props) => {
	const { locale } = useIntl();
	const windowIsDefined = typeof window === 'object';
	const [isOpenDropdown, setIsOpenDropdown] = useState(false);
	const [activeLink, setActiveLink] = useState(-1);

	const containerRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const linkRefs = useRef<HTMLButtonElement[]>([]);
	useOnClickOutside(containerRef, () => setIsOpenDropdown(false));

	const changePage = (language: string, path: string) => {
		if (path) {
			navigate(encodeURI(path));
		} else {
			navigate(language === Language.Default ? encodeURI(originalPath) : `/${language}/`);
		}
	}

	const switchLang = (language: string) => {
		if (language === locale) {
			return;
		}
		if (windowIsDefined) {
			window.localStorage.setItem(StorageCookie.CurrentLanguage, language);
		}
		const homePageLink = language === Language.Default ? '/' : `/${language}/`;

		if (originalPath === '/') {
			navigate(homePageLink);
			return;
		}

		switch (pageType) {
			case PageType.Events:
				changePage(language, routes[language]['/events/'] && `${routes[language]['/events/']}${originalPath}/`);
				break;
			case PageType.FAQ:
				changePage(language, faqRoutes[language][originalPath]);
				break;
			case PageType.Main:
			default:
				changePage(language, routes[language][originalPath]);
				break;
		}
	};

	useEffect(() => {
		if (windowIsDefined && window.localStorage.getItem(StorageCookie.CurrentLanguage) === Language.Lithuanian)
		{
			window.localStorage.setItem(StorageCookie.CurrentLanguage, Language.Default);
		}
		const localStorageLanguage = localStorage.getItem(StorageCookie.CurrentLanguage);
		if (windowIsDefined && localStorageLanguage !== null) {
			const url = window.location.pathname;
			// If it is the index page change the language in localStorage, oherwise show the language from the link
			if (url.length === 1) {
				if (locales.includes(localStorageLanguage)) {
					localStorage.setItem(StorageCookie.CurrentLanguage, localStorageLanguage);
					const search = window.location.search || "";
					navigate(localStorageLanguage === Language.Default ? `/${search}` : `/${localStorageLanguage}/${search}`);
				} else {
					localStorage.setItem(StorageCookie.CurrentLanguage, Language.Default);
					navigate('/');
				}
			} else if (url.length > 1) {
				localStorage.setItem(StorageCookie.CurrentLanguage, locale);
			}
		} else {
			// Language change on first visit
			const userLangs: readonly string[] =
				typeof navigator !== 'undefined'
					? navigator.languages ?? [navigator.language]
					: [Language.Default];

			let matchedLocale: string | undefined;

			userLangs.some((userLang) => {
				// Exact match of locale
				let userLocale = locales.find((code: string) => code === userLang);

				// If exact match doesn't exist try to find the beginning of the code (left-hand side)
				if (!userLocale) {
					const leftHandSide = userLang.replace(/-.{2}/, '');
					userLocale = locales.find((code: string) => code.startsWith(leftHandSide));
				}

				if (userLocale) {
					matchedLocale = userLocale;
					return true;
				}

				return false;
			});

			if (matchedLocale) {
				localStorage.setItem(StorageCookie.CurrentLanguage, matchedLocale);
				navigate(routes[matchedLocale][originalPath]);
				return;
			}

			localStorage.setItem(StorageCookie.CurrentLanguage, locale);
		}
	}, [locale, originalPath, windowIsDefined]);

	const isMobileOrTablet = useIsMobile(768);
	
	return (
		<div
			className={classnames(
				style.wrapper,
				{
					[style.popoverWrapper]: popover,
					[style.marginForArabicLang]: locale === Language.Arabic,
				}
			)}
			ref={containerRef}
			onKeyDown={(e) => {
				if (!buttonRef.current || !linkRefs.current) {
					return;
				}
				accessibleDropdownKeyboardHandler(e, buttonRef.current, linkRefs.current, isOpenDropdown, setIsOpenDropdown, activeLink, setActiveLink)
			}}
		>
			<div className={popover ? style.langPopoverWrapper : ''}>
				<button
					className={classnames(
						style.link, {
						[style.polishLink]: locale === Language.Polish,
						[style.active]: isOpenDropdown
					})}
					onClick={() => setIsOpenDropdown(!isOpenDropdown)}
					onFocus={() => setActiveLink(-1)}
					ref={buttonRef}
					aria-haspopup={true}
					aria-expanded={isOpenDropdown}
				>
					{languageMetadata[locale].title}
					<span className={classnames(style.arrow, {
						[style.rotate180]: isOpenDropdown,
						[style.arrowMarginForArabicLang]: locale === Language.Arabic
					})}>
						<svg xmlns="http://www.w3.org/2000/svg" width="11" height="7" viewBox="0 0 11 7">
							<path d="M6.432 6.114a1 1 0 0 1-1.519 0L1.087 1.651A1 1 0 0 1 1.847 0h7.651a1 1 0 0 1 .76 1.65z" />
						</svg>
					</span>
				</button>
				<div>
					<ul className={classnames(popover ? style.langPopoverBox : style.subMenu, {
						[(popover ? style.showBox : style.toggleSubmenu)]: isOpenDropdown,
						[style.ArabicPopupBox]: locale === Language.Arabic,
						[style.manyLangPopover]: locales.length > 6
					})}
					>
						{locales.map((language, index) => {
							// LT excluded, BNK-695
							if (language === Language.Lithuanian) {
								return <Fragment key={language}/>;
							}

							return (
								<li key={language}>
									<button
										onClick={() => {
											switchLang(language);
											setIsOpenDropdown(false);
										}}
										className={classnames(style.subLink, {
											[style.polishLinkSize]: language === Language.Polish,
											[style.manyLangSublink]: locales.length > 6,
											[style.mobileSublink]: isMobileOrTablet
										})}
										ref={(instance: HTMLButtonElement) => {
											if (index === 0) {
												linkRefs.current.length = 0;
											}
											linkRefs.current[index] = instance;
										}}
										onFocus={() => setActiveLink(index)}
									>
										<span>
											{languageMetadata[language].title}
										</span>
									</button>
								</li>
							);
						}
						)}
					</ul>
				</div>
			</div>
		</div>
	);
};

export default LanguageSwitcher;
